import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Outlet, useNavigate, useParams } from 'react-router';
import { ListingsPageView } from '../../../components/user/listings/ListingsPageView';
import { IListingRowProps } from '../../../components/user/listings/ListingsPageView/ListingTable.types';
import { Listing, WithID } from '../../../types/db';
import { ROUTE_PARAMS, ROUTE_PATH } from '../../../utilities/navigation';
import { UserSelectRoleContainer } from '../UserDashboardPage';
import { useUserListing } from '../../../hooks/listing/useUserListings';
import { useCurrentUserInfo } from '../../../hooks/useCurrentUserInfo';

/** The User own listing page */
export const UserListingsPage = () => {
    const navigate = useNavigate();
    const { [ROUTE_PARAMS.listingID]: listingID } = useParams();

    const navigateToListingPreview = (listingId: string) => {
        navigate(ROUTE_PATH.listingBuild(listingId, true));
    }
    const navigateToListingSettings = (listingId: string) => {
        navigate(ROUTE_PATH.listingSettingsBuild(listingId));
    }
    const navigateToListingCreation = (parentListingId?: string) => {
        navigate(ROUTE_PATH.listingNewBuild(parentListingId));
    }
    if (!listingID) {
        return <UserListingsContainer
            isFullPage={!listingID} //full page if no listingId indicated in the url
            navigateToListingPreview={navigateToListingPreview}
            navigateToListingSettings={navigateToListingSettings}
            navigateToListingCreation={navigateToListingCreation}
        />
    }
    return <Stack direction={'row'}>
        {/* Don't show side listings list for small screen */}
        <Box flex={1} display={{ xs: 'none', md: 'block' }}>
            <UserListingsContainer
                isFullPage={!listingID} //full page if no listingId indicated in the url
                navigateToListingPreview={navigateToListingPreview}
                navigateToListingSettings={navigateToListingSettings}
                navigateToListingCreation={navigateToListingCreation}
            />
        </Box>
        <Box flex={3}>
            <Outlet />
        </Box>
    </Stack>
}

interface IUserListingsContainerProps {
    /** Boolean indication if this is the root page, therefor full view, or not */
    isFullPage: boolean;
    /** Callback navigation to the listing preview */
    navigateToListingPreview: (listingId: string) => void;
    /** Callback navigation to the listing settings (will go by default to the first tab) */
    navigateToListingSettings: (listingId: string) => void;
    /** Callback navigation to the listing creation flow with optional parentId */
    navigateToListingCreation: (parentListingId?: string) => void
}

const UserListingsContainer = ({ isFullPage, navigateToListingPreview, navigateToListingSettings, navigateToListingCreation }: IUserListingsContainerProps) => {
    const { isLoading: isLoadingUser, user } = useCurrentUserInfo();
    const userId = user?.id;
    const userRole = user?.role;
    const { isLoading: isLoadingListings, listings, isError } = useUserListing(userId || '', (userRole !== true && userRole) || undefined);

    const overwrite = false;//trick to see the loading state (put back to false on prod)
    if (isLoadingUser || isLoadingListings || overwrite || userRole === true) {
        return <ListingsPageView
            // asRole={userRole}
            loading
            simplified={!isFullPage} />
    }

    if (isError) {
        //TODO have proper error page
        return <Typography>Something went wrong</Typography>
    }

    const mapping = (listing: WithID<Listing>): Omit<IListingRowProps, 'simplified'> => {
        const subListings = listing.sub_listings
            ?.map((sl) => {
                //getting the sublisting full info from data
                return listings.find(d => d.id === sl.id);
            })
            .filter((sl) => !!sl)//only get the one which got found
            ?? [];
        return {
            ...mapDataToUI(listing),
            onClick: () => navigateToListingSettings(listing.id),
            subListing: subListings.map(sl => mapping(sl!))
        }
    }

    const listProps =
        listings
            .filter((listing) =>
                (!listing.breadcrumbs?.length || !listing.parent_id) //only get the root listing
                && listing.status !== 'de-activated' //don't show the one in the process of deletion
            )
            .map((listing) => {
                return mapping(listing);
            }, [] as IListingRowProps[])

    const handleAddListingClick = () => {
        navigateToListingCreation();
    }

    if (!userRole) {
        return <UserSelectRoleContainer />
    }

    return <ListingsPageView
        // asRole={userRole}
        simplified={!isFullPage}
        items={listProps}
        onAddListingClick={handleAddListingClick} />
}

const mapDataToUI = (listing: WithID<Listing>): Omit<IListingRowProps, 'simplified'> => {
    return {
        key: listing.id,
        title: listing.title,
        image: listing.main_photo || '',
        lastModified: new Date(),
        status: listing.status === 'published' ? 'on' : 'off',
        location: listing.location?.address?.city || '',
    }
}